/**
 * Pagina recupero password.
 *
 * SICUREZZA — info leak prevention:
 * - Mostriamo SEMPRE lo stesso messaggio post-submit, sia che l'utente
 *   esista sia che no.
 * - La logica server-side (`requestPasswordReset` Cloud Function) decide
 *   se inviare effettivamente l'email; il client non riceve mai feedback
 *   sull'esistenza dell'account.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../security/firebase';
import { BrandPanel } from '../../components/ui/BrandPanel';
import { MobileLogo } from '../../components/ui/MobileLogo';

const schema = z.object({
  email: z.string().min(1, 'Inserisci l\'email').email('Email non valida'),
});

type Input = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Input>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: Input) => {
    setSubmitting(true);
    try {
      const fn = httpsCallable<Input, { ok: true }>(
        functions,
        'requestPasswordReset'
      );
      await fn({ email: data.email });
    } catch {
      // No-op: la risposta lato client è SEMPRE generica.
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch">
      <BrandPanel />
      <section className="flex-1 bg-white flex flex-col items-center justify-center p-6 md:p-margin-desktop">
        <MobileLogo />
        <div className="w-full max-w-[400px]">
          <header className="mb-10 text-center md:text-left">
            <h2 className="font-headline-lg text-headline-lg text-text-main mb-2">
              Recupera la password
            </h2>
            <p className="font-body-md text-text-secondary">
              Inserisci la tua email: se è registrata, riceverai un link per
              reimpostare la password.
            </p>
          </header>

          {submitted ? (
            <div
              role="status"
              className="p-4 rounded-lg bg-magenta-soft text-on-surface font-body-md"
            >
              Se l'indirizzo è registrato, riceverai un'email con le
              istruzioni nei prossimi minuti.
            </div>
          ) : (
            <form
              noValidate
              className="space-y-6"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div>
                <label
                  className="block font-label-sm text-on-surface-variant mb-2"
                  htmlFor="email"
                >
                  Email
                </label>
                <div className="relative group">
                  <span
                    className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline group-focus-within:text-primary"
                    aria-hidden="true"
                  >
                    mail
                  </span>
                  <input
                    {...register('email')}
                    id="email"
                    type="email"
                    autoComplete="username"
                    placeholder="Email"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    className="w-full h-12 pl-10 pr-4 rounded-lg border border-border-gray bg-white text-on-surface placeholder:text-outline-variant focus:outline-none focus:border-primary focus:ring-2 focus:ring-magenta-soft transition-all font-body-md"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-body-sm text-error" role="alert">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-primary text-on-primary font-body-md font-bold rounded-lg shadow-sm hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50"
              >
                {submitting ? 'Invio in corso…' : 'Invia link di reset'}
              </button>
            </form>
          )}

          <p className="mt-12 text-center font-body-sm">
            <Link
              to="/login"
              className="text-primary font-semibold hover:underline"
            >
              Torna al login
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}

export default ForgotPasswordPage;
